/**
 * Επεξεργαστής καταλόγου πλατφόρμας — efood (φάση 2β, docs/platform_name_mapping.md §13).
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: η υπεύθυνη στα Κεντρικά ανοίγει σήμερα 22 φορές το portal του
 * efood για κάθε αλλαγή τιμής. Εδώ εισάγουμε τον κατάλογο ΤΗΣ ΠΛΑΤΦΟΡΜΑΣ με τους
 * δικούς της κωδικούς, τον βλέπουμε ως ένα πλέγμα «είδη αλυσίδας × καταστήματα»,
 * και ετοιμάζουμε τις αλλαγές κεντρικά. ΔΕΝ δημοσιεύουμε τον κατάλογο του POS: οι
 * τιμές της πλατφόρμας είναι άλλες (δεσμευτική απόφαση χειριστή 16/9/2026).
 *
 * ΦΑΣΗ 5 (§13.19): η οθόνη ΣΤΕΛΝΕΙ ΠΛΕΟΝ ΠΡΟΣ ΤΑ ΕΞΩ. Τρεις νέες ενέργειες, και
 * οι τρεις πίσω από ανθρώπινη πύλη:
 *  • «Συγχρονισμός τιμών» — φτιάχνει ΠΡΟΧΕΙΡΕΣ αλλαγές τιμής από τον κατάλογό μας
 *    (κανάλι delivery, ΠΟΤΕ + τέλος πλαστικού) αφού ο χειριστής αποφασίσει ανά
 *    προϊόν. Καμία εξερχόμενη κλήση από μόνο του.
 *  • «Μετάπτωση» — ΟΛΙΚΗ αντικατάσταση του μενού ενός καταστήματος με δικούς μας
 *    κωδικούς. ΔΕΝ αναιρείται· ζητά να πληκτρολογηθεί το όνομα του καταστήματος.
 *  • «Αποστολή» — οι deltas, μόνο σε καταστήματα που έχουν ήδη μεταπτωθεί.
 *
 * ΤΙ ΔΕΝ ΚΑΝΕΙ ΠΟΤΕ: δεν αντιγράφει διαθεσιμότητα από το POS (§13.19.Δ — υπάρχει
 * κατάστημα με 157/183 είδη κλειστά στο efood επίτηδες) και δεν «διορθώνει» τιμή
 * χωρίς αποθηκευμένη απόφαση χειριστή.
 *
 * i18n: ελληνικά inline, ΧΩΡΙΣ useTranslation — συνειδητή επιλογή για συνέπεια με
 * τη γειτονική OrgPlatformMappingsTab.tsx (§13.11). Αν μεταφραστεί η μία,
 * μεταφράζονται μαζί.
 *
 * Νέα εμφάνιση (14/10/2026): όλη η καρτέλα σε ένα «φύλλο» όπως οι σελίδες «Πίνακας»
 * (lp-wrap/lp-sheet), ΧΩΡΙΣ δεύτερη κεφαλίδα — τίτλο, περιγραφή και ενέργειες τα έχει
 * ήδη το CatalogHeader. Υπο-καρτέλες με το SubTabs των σελίδων του οργανισμού, μηνύματα
 * με το Banner, σημειώσεις και σελιδοποίηση μόνο με tokens. Η λογική, τα effects και
 * οι κλήσεις ΔΕΝ άλλαξαν: άλλαξε μόνο ό,τι ζωγραφίζεται.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import {
  CatalogMode, CatalogNotice, CatalogSummary, EditChange, GridCell, GridParams, GridRow, GridStore,
  MigrationPreview, MigrationResult, Plan, PlatformName, PriceDiffResponse, PriceDiffRow,
  PriceOverride, PriceSyncHeader, RunDetail, RunSummary, SignedPlan, TwinDecisions, TwinsResponse,
  catalogNotice, isCatalogConflict, isTerminalRunStatus, orgPlatformCatalogAPI,
} from '../services/platformCatalogApi';
import CatalogHeader, { ImportProgress } from './platformCatalog/CatalogHeader';
import CatalogFilters, { DEFAULT_FILTERS, GridFilters } from './platformCatalog/CatalogFilters';
import CatalogGrid from './platformCatalog/CatalogGrid';
import BulkActionsBar from './platformCatalog/BulkActionsBar';
import DraftBasket from './platformCatalog/DraftBasket';
import DryRunModal from './platformCatalog/DryRunModal';
import MigrateModal from './platformCatalog/MigrateModal';
import MigrationTiles from './platformCatalog/MigrationTiles';
import OffersPanel from './platformCatalog/OffersPanel';
import PricesPanel from './platformCatalog/PricesPanel';
import PriceSyncModal from './platformCatalog/PriceSyncModal';
import RunHistory from './platformCatalog/RunHistory';
import {
  applyEditsToRows, buildPriceIndex, cellIsEditable, clearPendingInRows, defaultApplyToTwins,
  hasPendingDelete, platformAvailable, platformPrice, priceIndexComplete, runWatchStuck,
  shownAvailable, shownPrice,
} from './platformCatalog/catalogUi';
import useRunWatch from './platformCatalog/useRunWatch';
import { GHOST_BTN, ListPage, useListLayout } from './shared/listPage';
import { Banner, LINK_BTN, SubTab, SubTabs } from './orgPages/ui';

interface Props {
  orgId: number;
  /** org_owner / org_admin — ρυθμίσεις καταστήματος, αποστολή, αποφάσεις τιμής. */
  canManage?: boolean;
  /** canManage ή org_editor — εισαγωγή, πρόχειρες αλλαγές, στεγνή δοκιμή (§13.12). */
  canEdit: boolean;
  /**
   * ΜΟΝΟ ο ιδιοκτήτης τρέχει μετάπτωση (§13.19.Θ). Όσο ο ενορχηστρωτής δεν
   * περνά τον ρόλο ξεχωριστά, πέφτουμε πίσω στο `canManage` και την τελική
   * άρνηση τη λέει το 403 του server — ΜΟΝΙΜΟ μήνυμα, όχι κρυφό κουμπί που
   * αποτυγχάνει. Ποτέ το αντίστροφο: η οθόνη δεν ανοίγει ό,τι ο server κλείνει.
   */
  canOwn?: boolean;
}

type View = 'grid' | 'prices' | 'offers' | 'history';

/**
 * Πόσες γραμμές ζητάμε ανά σελίδα. Η ένωση δύο μόνο καταστημάτων δίνει ήδη 189
 * είδη αλυσίδας (§13.1)· με 300 χωρά ολόκληρος ο κατάλογος σε μία σελίδα και το
 * πλέγμα δεν «σπάει» σε σελίδες στη μέση μιας κατηγορίας.
 */
const GRID_PAGE = 300;

/** Πόσο αργεί η αναζήτηση να φύγει στο backend (ένα γράμμα ≠ ένα ερώτημα). */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Πόσες γραμμές σύγκρισης τιμής φέρνουμε με τη μία.
 *
 * ΓΙΑΤΙ ΤΟΣΟ ΜΕΓΑΛΟ: η σύγκριση δεν έχει σελιδοποίηση στην οθόνη — ή την ξέρεις
 * ΟΛΗ ή το πλέγμα λέει ψέματα. Με 20 καταστήματα × 183 είδη + μεγέθη οι γραμμές
 * με διαφορά είναι ~350· ολόκληρη η σύγκριση είναι ~4.000.
 *
 * ΓΙΑΤΙ ΑΚΡΙΒΩΣ 6000: είναι το ΔΙΚΟ ΤΟΥ ανώτατο όριο της `GET …/price-diff`
 * (§11.1). Το γενικό `MAX_LIMIT` των υπόλοιπων διαδρομών είναι 500 και το
 * `readLimit` κόβει ΣΙΩΠΗΛΑ — ζητώντας 5.000 παίρναμε 500 γραμμές και νομίζαμε
 * ότι έχουμε όλη τη σύγκριση. Ο server απαντά πάντα `total` + `truncated`· αν
 * κοπεί, το λέμε ρητά και το ευρετήριο ΔΕΝ γίνεται «πλήρες».
 */
const PRICE_PAGE = 6000;

/**
 * Το αίτημα δεν απαντήθηκε: αυτό ΔΕΝ είναι απόδειξη ότι δεν έφυγε τίποτα.
 *
 * Από τη Φ5 η αποστολή και η μετάπτωση απαντούν 202 και συνεχίζουν μόνες τους
 * στον server. Ένα κομμένο socket (proxy, κλείσιμο laptop, δίκτυο) σημαίνει
 * «δεν ξέρω», όχι «απέτυχε» — και η μόνη σωστή επόμενη κίνηση είναι το
 * «Ιστορικό», ποτέ δεύτερο πάτημα πάνω σε μη αναστρέψιμη κλήση.
 */
const TRANSPORT_TEXT = 'Το αίτημα δεν απαντήθηκε (δίκτυο ή διακοπή). Αν η εκτέλεση πρόλαβε να ξεκινήσει, '
  + 'ΣΥΝΕΧΙΖΕΙ στον server: δες πρώτα το «Ιστορικό» και ΜΗΝ ξαναστείλεις.';

const isTransportError = (err: any): boolean => !err || !err.response;

/**
 * Μήνυμα αποτυχίας αποστολής. Το 409 `confirm_stale` παίρνει ΚΑΙ τη διέξοδο:
 * το σχέδιο του συγχρονισμού τιμών και το καλάθι μπορεί να περιγράφουν
 * διαφορετικά σύνολα αλλαγών, και χωρίς αυτή τη γραμμή ο χειριστής ξαναπατά το
 * ίδιο κουμπί για πάντα.
 */
const pushFailureText = (err: any): string => {
  if (isTransportError(err)) return TRANSPORT_TEXT;
  const base = catalogNotice(err, 'Αποτυχία αποστολής').text;
  return isCatalogConflict(err)
    ? `${base} Πάτησε «Νέα δοκιμή» εδώ μέσα: το σχέδιο ξαναϋπολογίζεται πάνω σε ό,τι είναι ανοιχτό τώρα.`
    : base;
};

/** Ποιο κατάστημα φεύγει ΠΡΩΤΟ, όπως το λέει το ίδιο το σχέδιο (§13.19.ΙΓ β.5). */
const canaryOf = (p: SignedPlan): number | null => {
  const jobs = p.jobs || [];
  const marked = jobs.filter(j => j.canary)[0];
  const first = marked || jobs[0];
  return first ? first.store_id : null;
};

/** Κλειδί απόφασης: ΠΑΝΤΑ το κεντρικό είδος — τα item_code αλλάζουν στη μετάπτωση. */
const overrideKey = (o: PriceOverride): string => o.master_key;

/** Είναι η ίδια απόφαση; (για να μη μετράμε «αλλαγμένο» ό,τι γύρισε στην αρχή του) */
const sameOverride = (a: PriceOverride, b: PriceOverride): boolean => {
  if (a.mode !== b.mode) return false;
  const an = (a.note || '').trim();
  const bn = (b.note || '').trim();
  if (an !== bn) return false;
  if (a.mode !== 'fixed') return true;
  const ap = (a.fixed_price === null || a.fixed_price === undefined) ? null : Number(a.fixed_price);
  const bp = (b.fixed_price === null || b.fixed_price === undefined) ? null : Number(b.fixed_price);
  if (ap === null || bp === null) return ap === bp;
  return Math.abs(ap - bp) < 0.005;
};

/**
 * Η διαδρομή δεν υπάρχει σε αυτόν τον server (404/501) — ΔΕΝ είναι βλάβη.
 *
 * Οι Φ1-Φ4 τρέχουν ζωντανά και στους δύο παραγωγικούς servers· μέχρι να ανέβει
 * η Φ5, το `price-diff` απαντά 404. Μια άδεια οθόνη θα διαβαζόταν ως «όλες οι
 * τιμές συμφωνούν», που είναι το χειρότερο δυνατό ψέμα εδώ.
 */
const isMissingRoute = (err: any): boolean => {
  const status = err && err.response ? err.response.status : 0;
  return status === 404 || status === 501;
};

/**
 * Η κατάσταση του διακόπτη όπως τη δηλώνει το 403 της πύλης (§13.12,
 * orgPlatformCatalog.js: `res.status(403).json({ …, mode })`). Επιστρέφει null σε
 * κάθε άλλο σφάλμα — δίκτυο, ρόλος, βλάβη.
 */
const gateModeOf = (err: any): CatalogMode | null => {
  const raw = err && err.response && err.response.data ? err.response.data.mode : null;
  return (raw === 'off' || raw === 'read' || raw === 'edit') ? raw : null;
};

/**
 * Οι κατηγορίες που προσφέρει ο επιλογέας φίλτρου.
 *
 * ΓΙΑΤΙ ΔΕΝ βγαίνουν σκέτα από τις ορατές γραμμές: μόλις ο χειριστής διαλέξει
 * «Καφέδες», το πλέγμα γυρίζει μόνο καφέδες — άρα ο επιλογέας θα έμενε με μία
 * κατηγορία και το πέρασμα στα «Κουλούρια» θα απαιτούσε πρώτα επιστροφή στο
 * «Όλες». Κρατάμε λοιπόν σωρευτική λίστα: ΑΝΤΙΚΑΘΙΣΤΑΤΑΙ όταν δεν υπάρχει
 * φίλτρο κατηγορίας (τότε η σελίδα είναι αντιπροσωπευτική) και ΕΝΩΝΕΤΑΙ όσο
 * υπάρχει. Οι κενές ετικέτες δεν μπαίνουν: το backend φιλτράρει με
 * `LOWER(category_label)` και μια πλασματική «Χωρίς κατηγορία» δεν ταιριάζει πουθενά.
 */
const categoriesOf = (rows: GridRow[]): string[] => {
  const seen: Record<string, boolean> = {};
  const out: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const label = rows[i].category_label;
    if (!label || seen[label]) continue;
    seen[label] = true;
    out.push(label);
  }
  return out;
};

const mergeCategories = (prev: string[], rows: GridRow[], replace: boolean): string[] => {
  const fresh = categoriesOf(rows);
  const out = replace ? fresh.slice() : prev.slice();
  if (!replace) {
    for (let i = 0; i < fresh.length; i++) if (out.indexOf(fresh[i]) === -1) out.push(fresh[i]);
  }
  return out.sort((a, b) => a.localeCompare(b, 'el'));
};

/** Τίμησε το σχέδιο τις αποφάσεις διδύμων που ζητήθηκαν; */
const planHonoursTwinDecisions = (result: Plan, decisions: TwinDecisions): boolean => {
  const twins = result.twins || [];
  for (let i = 0; i < twins.length; i++) {
    const t = twins[i];
    const perStore = decisions[String(t.store_id)];
    if (!perStore) continue;
    const want = perStore[t.twin_key];
    if (want === undefined) continue;
    if (!!t.apply_to_all !== !!want) return false;
  }
  return true;
};

const gridParams = (platform: PlatformName, filters: GridFilters, offset: number): GridParams => {
  const params: GridParams = { platform, limit: GRID_PAGE, offset };
  if (filters.category) params.category = filters.category;
  const q = filters.q.trim();
  if (q) params.q = q;
  if (filters.only_unavailable) params.only_unavailable = 1;
  if (filters.price_differs) params.price_differs = 1;
  if (filters.missing_in) params.missing_in = 1;
  if (filters.has_edits) params.has_edits = 1;
  if (filters.structure_differs) params.structure_differs = 1;
  if (filters.twins_only) params.twins_only = 1;
  // ΠΑΝΤΑ ρητό (και το 0): η προεπιλογή του backend δεν πρέπει να αποφασίζει τι
  // βλέπει ο χειριστής όταν ξετσεκάρει το φίλτρο.
  params.hide_offer_items = filters.hide_offer_items ? 1 : 0;
  return params;
};

/**
 * Το «φύλλο» γύρω από την καρτέλα: ίδιες κλάσεις με τη ListPage (lp-wrap/lp-sheet), αλλά
 * ΧΩΡΙΣ κεφαλίδα — το CatalogHeader έχει ήδη τίτλο, περιγραφή και ενέργειες, και δεύτερος
 * τίτλος από πάνω του θα έλεγε το ίδιο πράγμα δύο φορές.
 *
 * ΓΙΑΤΙ δικό του component: το data-layout (wide/medium/narrow) μετριέται εδώ μέσα, ώστε
 * ένα resize να ξαναζωγραφίζει ΜΟΝΟ το φύλλο — τα παιδιά έρχονται ως ίδια στοιχεία και το
 * πλέγμα με τα ~500 κελιά δεν ξαναφτιάχνεται.
 */
const CatalogSheet: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const layout = useListLayout(ref);
  return (
    <div ref={ref} className="lp-wrap" data-layout={layout} data-testid="org-platform-catalog">
      <div className="lp-sheet space-y-4">{children}</div>
    </div>
  );
};

const OrgPlatformCatalogTab: React.FC<Props> = ({ orgId, canManage = false, canEdit, canOwn }) => {
  const [platform, setPlatform] = useState<PlatformName>('efood');
  const [view, setView] = useState<View>('grid');
  const [notice, setNotice] = useState<CatalogNotice | null>(null);

  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  /**
   * Η κατάσταση που δήλωσε το 403 της πύλης (§13.12). ΓΙΑΤΙ ξεχωριστό state: όταν
   * ο διακόπτης είναι 'off', το `GET …/summary` ΔΕΝ γυρίζει 200 με mode:'off' —
   * κόβεται από το middleware με 403 `{error, mode}`. Χωρίς αυτό, η σύνοψη μένει
   * null, η οθόνη υποθέτει «ανάγνωση» και δείχνει ολόκληρο τον επεξεργαστή να
   * γεμίζει κόκκινα μηνύματα, αντί για την ήρεμη εξήγηση ότι το χαρακτηριστικό
   * είναι κλειστό.
   */
  const [gateMode, setGateMode] = useState<CatalogMode | null>(null);

  const [rows, setRows] = useState<GridRow[]>([]);
  const [stores, setStores] = useState<GridStore[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);

  const [filters, setFilters] = useState<GridFilters>(DEFAULT_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState('');
  /** Σωρευτική λίστα κατηγοριών για τον επιλογέα (δες mergeCategories). */
  const [knownCategories, setKnownCategories] = useState<string[]>([]);

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [selectedStores, setSelectedStores] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * ΜΟΝΟ υπογεγραμμένο σχέδιο μπαίνει εδώ (`run` γεμάτο). Ο `price-sync` μπορεί
   * να απαντήσει `run: null` («καμία τιμή δεν χρειάζεται αλλαγή») και ΑΥΤΟ ΔΕΝ
   * είναι σχέδιο: το παράθυρο επιβεβαίωσης θα διάβαζε `plan.run.confirm_hash`
   * πάνω σε null και θα ξεμοντάριζε ολόκληρη την καρτέλα (δεν υπάρχει
   * ErrorBoundary στο src/) — λευκή οθόνη με F5.
   */
  const [plan, setPlan] = useState<SignedPlan | null>(null);
  /** Ζητήθηκαν αποφάσεις διδύμων και το σχέδιο γύρισε χωρίς να τις λάβει υπόψη. */
  const [twinDecisionsIgnored, setTwinDecisionsIgnored] = useState(false);

  const [offersStore, setOffersStore] = useState<number | null>(null);
  const [twins, setTwins] = useState<TwinsResponse | null>(null);
  const [twinsLoading, setTwinsLoading] = useState(false);
  const [worklistBusy, setWorklistBusy] = useState(false);

  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runId, setRunId] = useState<number | null>(null);
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  const [gridHeight, setGridHeight] = useState(520);

  // ── Φ5: τιμές, αποφάσεις, μετάπτωση, αποστολή ──────────────────────────

  const [priceRows, setPriceRows] = useState<PriceDiffRow[]>([]);
  const [priceSummary, setPriceSummary] = useState<Record<string, unknown> | null>(null);
  const [priceTotal, setPriceTotal] = useState(0);
  /** Ο server έκοψε τη λίστα: ό,τι λείπει ΔΕΝ σημαίνει «συμφωνούν». */
  const [priceTruncated, setPriceTruncated] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  /**
   * Ζητήθηκε η σύγκριση σε αυτή τη συνεδρία;
   *
   * ΓΙΑΤΙ ΔΕΝ ΦΟΡΤΩΝΕΙ ΜΟΝΗ ΤΗΣ: το `price-diff` συγκρίνει κατάστημα-κατάστημα
   * (2 ερωτήματα πλέγματος + 7 τιμολόγησης ανά κατάστημα — ~180 round trips και
   * ~16.000 γραμμές για 20 καταστήματα). Πριν, έφευγε σε ΚΑΘΕ άνοιγμα της
   * καρτέλας, ακόμη και για θεατή που δεν πάτησε ποτέ «Τιμές».
   */
  const [priceWanted, setPriceWanted] = useState(false);
  /**
   * Μνήμη ανά (πλατφόρμα, κατάστημα, φίλτρο): το πήγαινε-έλα «όλα → κατ. 23 →
   * όλα» δεν ξαναρωτά. Το `force` (αποθήκευση αποφάσεων, μετάπτωση, αποστολή)
   * την αδειάζει ολόκληρη — εκεί άλλαξαν πραγματικά τα δεδομένα.
   */
  const priceCache = useRef<Record<string, PriceDiffResponse>>({});
  /**
   * Ποιον συνδυασμό ζητάμε ΑΥΤΗ ΤΗ ΣΤΙΓΜΗ. Το ρητό πάτημα του χειριστή και το
   * effect των φίλτρων μπορούν να ζητήσουν το ίδιο πράγμα στο ίδιο render —
   * χωρίς αυτό, το ένα κλικ γινόταν δύο φορές ~180 ερωτήματα.
   */
  const priceInFlight = useRef<string | null>(null);
  /** Ο server δεν έχει (ακόμη) τη Φ5 — ΜΟΝΙΜΟ μήνυμα, ποτέ σιωπηλά άδειος πίνακας. */
  const [priceUnavailable, setPriceUnavailable] = useState<string | null>(null);
  const [priceStoreId, setPriceStoreId] = useState<number | null>(null);
  /**
   * Εξ ορισμού ΜΟΝΟ οι διαφορές: είναι ~350 γραμμές αντί για ~4.000, και είναι
   * αυτές που χρειάζεται το σήμα του πλέγματος. Όταν ο χειριστής ζητήσει όλη τη
   * σύγκριση, το ευρετήριο γίνεται `complete` και τότε —και μόνο τότε— το πλέγμα
   * επιτρέπεται να γράψει «ίδια» στη στήλη του καταλόγου.
   */
  const [priceOnlyMismatch, setPriceOnlyMismatch] = useState(true);
  const [priceQ, setPriceQ] = useState('');

  const [overrides, setOverrides] = useState<Record<string, PriceOverride>>({});
  /** Ό,τι άλλαξε ο χειριστής και ΔΕΝ έχει σταλεί ακόμη στο `PUT …/price-overrides`. */
  const [overrideDrafts, setOverrideDrafts] = useState<Record<string, PriceOverride>>({});
  const [overridesSaving, setOverridesSaving] = useState(false);

  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  /** Η κεφαλίδα του τελευταίου συγχρονισμού — ταξιδεύει μαζί με το σχέδιο. */
  const [syncHeader, setSyncHeader] = useState<PriceSyncHeader | null>(null);

  const [migrateStoreId, setMigrateStoreId] = useState<number | null>(null);
  const [migratePreview, setMigratePreview] = useState<MigrationPreview | null>(null);
  const [migratePreviewLoading, setMigratePreviewLoading] = useState(false);
  const [migrateRunning, setMigrateRunning] = useState(false);
  const [migrateResult, setMigrateResult] = useState<MigrationResult | null>(null);
  const [migrateError, setMigrateError] = useState<string | null>(null);

  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  /** Η εκτέλεση που τρέχει ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ και παρακολουθείται με ερωτήματα. */
  const [pushRunId, setPushRunId] = useState<number | null>(null);
  const [pushCanaryStoreId, setPushCanaryStoreId] = useState<number | null>(null);
  const [migrateRunId, setMigrateRunId] = useState<number | null>(null);
  /**
   * Το κατάστημα με μετάπτωση που ΤΡΕΧΕΙ ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ, ακόμη κι αν το
   * παράθυρο έκλεισε. Κρατά το πλακίδιο κλειδωμένο: δεύτερο ingest στο ίδιο
   * κατάστημα ΔΕΝ ξεκινά ποτέ από την οθόνη (ο server το κόβει κιόλας με
   * `lock_not_held`, αλλά η οθόνη δεν προσφέρει κουμπί που δεν πρέπει να πατηθεί).
   */
  const [migrateBgStoreId, setMigrateBgStoreId] = useState<number | null>(null);

  /**
   * Οι τρέχουσες γραμμές, διαβάσιμες μέσα από callback που ΔΕΝ πρέπει να
   * ξαναφτιάχνεται σε κάθε φόρτωση πλέγματος: κάθε νέα ταυτότητα callback
   * ξαναζωγραφίζει και τα ~500 ορατά κελιά.
   */
  const rowsRef = useRef<GridRow[]>([]);
  rowsRef.current = rows;

  const mode: CatalogMode = summary ? summary.mode : (gateMode || 'read');
  const editingAllowed = canEdit && mode === 'edit';
  /**
   * Ποιος πληρώνει αυτόματα τη σύγκριση τιμών: μόνο όποιος μπορεί να την
   * αξιοποιήσει. Ο θεατής τη ζητά ρητά (κουμπί «Φόρτωση σύγκρισης») — δεν του
   * απαγορεύεται, απλώς δεν φεύγει από μόνη της για κάθε κλικ στην καρτέλα.
   */
  const canUsePrices = canEdit || canManage;

  // ── Φορτώσεις ───────────────────────────────────────────────────────────

  const loadSummary = useCallback(async (silent: boolean) => {
    if (!silent) setSummaryLoading(true);
    try {
      const data = await orgPlatformCatalogAPI.getSummary(orgId, platform);
      setSummary(data);
      setGateMode(null);
    } catch (err: any) {
      setSummary(null);
      // Το 403 της πύλης κουβαλά τη ΔΙΚΗ του κατάσταση· κλειστός διακόπτης δεν
      // είναι βλάβη και δεν θέλει κόκκινο μήνυμα — θέλει την οθόνη-κλειδαριά.
      const gate = gateModeOf(err);
      setGateMode(gate);
      if (gate !== 'off') setNotice(catalogNotice(err, 'Αποτυχία φόρτωσης σύνοψης καταλόγου'));
      else setNotice(null);
    } finally {
      if (!silent) setSummaryLoading(false);
    }
  }, [orgId, platform]);

  const loadGrid = useCallback(async (nextOffset: number, currentFilters: GridFilters) => {
    setGridLoading(true);
    try {
      const data = await orgPlatformCatalogAPI.getGrid(orgId, gridParams(platform, currentFilters, nextOffset));
      setRows(data.rows || []);
      setStores(data.stores || []);
      setTotal(Number(data.total) || 0);
      setKnownCategories(prev => mergeCategories(prev, data.rows || [], !currentFilters.category));
    } catch (err: any) {
      setRows([]);
      // Κλειστός διακόπτης: το λέει ΜΟΝΟ η οθόνη-κλειδαριά, μία φορά — όχι άλλο
      // ένα μήνυμα από κάθε φόρτωση που κόβεται από την ίδια πύλη.
      const gate = gateModeOf(err);
      if (gate === 'off') setGateMode('off');
      else setNotice(catalogNotice(err, 'Αποτυχία φόρτωσης πλέγματος'));
    } finally {
      setGridLoading(false);
    }
  }, [orgId, platform]);

  /**
   * Η σύγκριση «τιμή efood ↔ τιμή καταλόγου (delivery)» (§13.19.Α).
   *
   * ΚΑΜΙΑ εξερχόμενη κλήση: διαβάζει το τελευταίο στιγμιότυπο που έφερε η
   * εισαγωγή. Γι' αυτό μπορεί να τρέχει σιωπηλά στο παρασκήνιο — δεν κοστίζει
   * τίποτα στο efood και δεν μετράει στο όριο του ενός αιτήματος/15΄.
   */
  const applyPrices = useCallback((data: PriceDiffResponse) => {
    setPriceRows(data.rows);
    setPriceSummary(data.summary);
    setPriceTotal(data.total);
    setPriceTruncated(data.truncated);
    // Μισή αλήθεια = ψέμα: αν κόπηκε η λίστα, ο χειριστής πρέπει να το ξέρει
    // πριν αποφασίσει ότι «δεν υπάρχουν άλλες διαφορές».
    // Το «περιόρισε σε ένα κατάστημα» ΔΕΝ δίνει πλήρη εικόνα στο ΠΛΕΓΜΑ, που
    // δείχνει ΟΛΑ τα καταστήματα: δίνει τις υπόλοιπες διαφορές ΕΚΕΙΝΟΥ του
    // καταστήματος. Παλιότερα το μήνυμα υποσχόταν το αντίθετο και οδηγούσε τον
    // χειριστή ακριβώς στο ψέμα που προσπαθούσε να αποτρέψει.
    setPriceUnavailable(data.truncated
      ? `Η σύγκριση κόπηκε στις ${data.rows.length} από ${data.total} γραμμές. `
        + 'Διάλεξε ένα κατάστημα για να δεις τις υπόλοιπες ΔΙΚΕΣ ΤΟΥ διαφορές — το πλέγμα '
        + 'όμως δείχνει ΟΛΑ τα καταστήματα, και όσο η σύγκριση δεν είναι πλήρης για '
        + 'ΟΛΟΝ τον οργανισμό γράφει «—», ποτέ «ίδια».'
      : null);
  }, []);

  const loadPrices = useCallback(async (silent: boolean, force?: boolean) => {
    const key = `${platform}|${priceStoreId === null ? 'all' : priceStoreId}|${priceOnlyMismatch ? 1 : 0}`;
    if (force) priceCache.current = {};
    const cached = priceCache.current[key];
    if (cached) { applyPrices(cached); return; }
    if (!force && priceInFlight.current === key) return;
    priceInFlight.current = key;
    if (!silent) setPriceLoading(true);
    try {
      const data = await orgPlatformCatalogAPI.getPriceDiff(orgId, {
        platform,
        store_id: priceStoreId === null ? undefined : priceStoreId,
        only_mismatch: priceOnlyMismatch,
        limit: PRICE_PAGE,
        offset: 0,
      });
      priceCache.current[key] = data;
      applyPrices(data);
    } catch (err: any) {
      setPriceRows([]);
      setPriceSummary(null);
      setPriceTotal(0);
      setPriceTruncated(false);
      if (isMissingRoute(err)) {
        setPriceUnavailable('Αυτός ο server δεν έχει ακόμη τη σύγκριση τιμών (φάση 5). '
          + 'Ο πίνακας μένει άδειος ΕΠΕΙΔΗ δεν ρωτήθηκε κανείς — όχι επειδή συμφωνούν οι τιμές.');
      } else if (gateModeOf(err) === 'off') {
        setGateMode('off');
      } else {
        setPriceUnavailable(catalogNotice(err, 'Αποτυχία σύγκρισης τιμών').text);
      }
    } finally {
      if (priceInFlight.current === key) priceInFlight.current = null;
      if (!silent) setPriceLoading(false);
    }
  }, [orgId, platform, priceStoreId, priceOnlyMismatch, applyPrices]);

  /** Ρητό αίτημα του χειριστή: «θέλω τη σύγκριση τώρα» (και φρέσκια). */
  const requestPrices = useCallback(() => {
    setPriceWanted(true);
    loadPrices(false, true);
  }, [loadPrices]);

  /** Οι ΜΟΝΙΜΕΣ αποφάσεις. Ό,τι λείπει από εδώ σημαίνει «ακολουθεί τον κατάλογο». */
  const loadOverrides = useCallback(async () => {
    try {
      const list = await orgPlatformCatalogAPI.getPriceOverrides(orgId, platform);
      const map: Record<string, PriceOverride> = {};
      for (let i = 0; i < list.length; i++) {
        if (list[i] && list[i].master_key) map[overrideKey(list[i])] = list[i];
      }
      setOverrides(map);
    } catch (err: any) {
      // Σιωπηλά: η απουσία αποφάσεων ΕΙΝΑΙ έγκυρη κατάσταση (όλα «κατάλογος»)
      // και η πραγματική εξήγηση έρχεται ήδη από το `price-diff`.
      setOverrides({});
    }
  }, [orgId, platform]);

  useEffect(() => { loadSummary(false); }, [loadSummary]);

  /**
   * Η σύγκριση φορτώνεται ΚΑΙ για το πλέγμα (στήλη «Κατάλογος»), όχι μόνο για
   * την υπο-καρτέλα — αλλά ΜΟΝΟ αφού ζητηθεί μία φορά (`priceWanted`). Από εκεί
   * και πέρα ακολουθεί τα φίλτρα, με μνήμη ανά συνδυασμό. Δεν τρέχει όταν ο
   * διακόπτης είναι κλειστός: εκεί η οθόνη είναι η κλειδαριά και τίποτε άλλο.
   */
  useEffect(() => {
    if (gateMode === 'off') return;
    if (!priceWanted) return;
    loadPrices(true);
  }, [loadPrices, gateMode, priceWanted]);

  /** Το άνοιγμα της υπο-καρτέλας «Τιμές» ΕΙΝΑΙ το αίτημα για τη σύγκριση. */
  useEffect(() => {
    if (view === 'prices' && canUsePrices) setPriceWanted(true);
  }, [view, canUsePrices]);

  useEffect(() => {
    if (gateMode === 'off') return;
    loadOverrides();
  }, [loadOverrides, gateMode]);

  // Άλλη πλατφόρμα = άλλες αποφάσεις και άλλες τιμές: ό,τι δεν αποθηκεύτηκε
  // ΔΕΝ επιτρέπεται να μεταφερθεί σε κατάλογο άλλης πλατφόρμας.
  useEffect(() => {
    setOverrideDrafts({});
    setPriceStoreId(null);
    setPriceQ('');
    // Άλλη πλατφόρμα = άλλες γραμμές σύγκρισης: η μνήμη δεν μεταφέρεται.
    priceCache.current = {};
    setPriceRows([]);
    setPriceSummary(null);
    setPriceTotal(0);
    setPriceTruncated(false);
  }, [platform]);

  // Η αναζήτηση φεύγει με καθυστέρηση· τα υπόλοιπα φίλτρα είναι διακόπτες και
  // φεύγουν αμέσως.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(filters.q), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [filters.q]);

  useEffect(() => {
    setOffset(0);
    loadGrid(0, { ...filters, q: debouncedQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadGrid, debouncedQ, filters.category, filters.only_unavailable, filters.price_differs,
    filters.missing_in, filters.has_edits, filters.structure_differs, filters.twins_only,
    filters.hide_offer_items,
  ]);

  // Το πλέγμα παίρνει ό,τι ύψος περισσεύει: με 22 στήλες η κάθετη κύλιση είναι η
  // δουλειά της οθόνης, όχι η κύλιση της σελίδας.
  useEffect(() => {
    const measure = () => setGridHeight(Math.max(320, window.innerHeight - 430));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const loadTwins = useCallback(async (storeId: number) => {
    setTwinsLoading(true);
    try {
      const data = await orgPlatformCatalogAPI.getTwins(orgId, platform, storeId);
      setTwins(data);
    } catch (err: any) {
      setTwins(null);
      setNotice(catalogNotice(err, 'Αποτυχία φόρτωσης προσφορών/διδύμων'));
    } finally {
      setTwinsLoading(false);
    }
  }, [orgId, platform]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      setRuns(await orgPlatformCatalogAPI.listRuns(orgId, platform));
    } catch (err: any) {
      setRuns([]);
      setNotice(catalogNotice(err, 'Αποτυχία φόρτωσης ιστορικού'));
    } finally {
      setRunsLoading(false);
    }
  }, [orgId, platform]);

  useEffect(() => {
    if (view === 'history') loadRuns();
  }, [view, loadRuns]);

  /**
   * Οθόνη προόδου (§13.11): όσο υπάρχει εκτέλεση σε εξέλιξη, η λίστα ανανεώνεται
   * ανά 2΄΄. ΓΙΑΤΙ polling και όχι websocket: ο backend τρέχει σε cluster με δύο
   * διεργασίες χωρίς κοινή μνήμη (§13.8.4) — η κατάσταση ζει στη βάση, όχι σε
   * σύνδεση. Σε αυτή την έκδοση δεν παράγονται εκτελέσεις (καμία αποστολή), αλλά
   * ο βρόχος μένει ώστε η Φ5 να μη χρειαστεί άλλη οθόνη.
   */
  const liveRuns = runs.some(r => r.status === 'pending' || r.status === 'running');
  useEffect(() => {
    if (view !== 'history' || !liveRuns) return;
    const id = window.setInterval(() => { loadRuns(); }, 2000);
    return () => window.clearInterval(id);
  }, [view, liveRuns, loadRuns]);

  useEffect(() => {
    if (view === 'offers' && offersStore !== null) loadTwins(offersStore);
  }, [view, offersStore, loadTwins]);

  // ── Εισαγωγή ────────────────────────────────────────────────────────────

  /**
   * Σειριακά, ΕΝΑ κατάστημα ανά αίτημα.
   *
   * ΓΙΑΤΙ όχι ένα αίτημα με όλα τα store_ids: το backend παίρνει advisory lock ανά
   * κατάστημα και η εισαγωγή 22 καταστημάτων κρατά λεπτά — ένα αίτημα θα έδειχνε
   * μια μπάρα που δεν κουνιέται και θα κινδύνευε με timeout του proxy. Έτσι, η
   * πρόοδος είναι πραγματική και ένα κατάστημα που αποτυγχάνει δεν ρίχνει τα άλλα.
   */
  const importStores = useCallback(async (storeIds: number[]) => {
    if (storeIds.length === 0 || importing) return;
    const names: Record<number, string> = {};
    (summary ? summary.stores : []).forEach(s => { names[s.store_id] = s.store_name; });

    setImporting(true);
    setNotice(null);
    let failed = 0;
    let unchanged = 0;
    let lastError = '';
    for (let i = 0; i < storeIds.length; i++) {
      const storeId = storeIds[i];
      setProgress({ total: storeIds.length, done: i, current: names[storeId] || `#${storeId}`, failed });
      try {
        const report = await orgPlatformCatalogAPI.runImport(orgId, platform, [storeId]);
        const result = report && report.results ? report.results[0] : null;
        if (result && result.ok === false) {
          failed += 1;
          lastError = result.error || '';
        } else if (result && result.unchanged) {
          unchanged += 1;
        }
      } catch (err: any) {
        failed += 1;
        const info = catalogNotice(err, 'Αποτυχία εισαγωγής');
        lastError = info.text;
      }
    }
    setProgress({ total: storeIds.length, done: storeIds.length, current: null, failed });
    setImporting(false);

    const ok = storeIds.length - failed;
    setNotice(failed > 0
      ? { kind: 'warn', text: `Εισαγωγή: ${ok} επιτυχίες, ${failed} αποτυχίες${lastError ? ` — ${lastError}` : ''}` }
      : { kind: 'ok', text: `Εισαγωγή ολοκληρώθηκε για ${ok} ${ok === 1 ? 'κατάστημα' : 'καταστήματα'}${unchanged > 0 ? ` (${unchanged} χωρίς αλλαγή)` : ''}.` });

    await loadSummary(true);
    await loadGrid(offset, { ...filters, q: debouncedQ });
  }, [importing, summary, orgId, platform, loadSummary, loadGrid, offset, filters, debouncedQ]);

  const importAll = useCallback(() => {
    const ids = (summary ? summary.stores : []).filter(s => s.has_credentials).map(s => s.store_id);
    importStores(ids);
  }, [summary, importStores]);

  // ── Αλλαγές ─────────────────────────────────────────────────────────────

  const submitChanges = useCallback(async (changes: EditChange[]) => {
    if (changes.length === 0 || !editingAllowed) return;
    setBusy(true);
    try {
      const report = await orgPlatformCatalogAPI.createEdits(orgId, platform, changes);
      setRows(prev => applyEditsToRows(prev, report.edits || []));
      const twinRows = Number(report.twin_rows) || 0;
      const skippedRows = report.skipped || [];
      const skipped = skippedRows.length;
      const written = (Number(report.created) || 0) + (Number(report.updated) || 0);
      // Όταν ΤΙΠΟΤΑ δεν γράφτηκε, ο αριθμός δεν λέει τίποτα — ο λόγος λέει. Η
      // συχνότερη περίπτωση: επιστροφή στην τιμή του efood, που αφήνει την
      // πρόχειρη αλλαγή ζωντανή (σβήνει μόνο με «Καθαρισμός αλλαγών»).
      const firstReason = skipped > 0 ? (skippedRows[0].message || skippedRows[0].code || '') : '';
      setNotice({
        kind: skipped > 0 ? 'warn' : 'ok',
        text: `Αποθηκεύτηκαν ${Number(report.created) || 0} νέες και ${Number(report.updated) || 0} ενημερωμένες αλλαγές`
          + (twinRows > 0 ? `, μαζί με ${twinRows} δίδυμα` : '')
          + (skipped > 0 ? `. ${skipped} παραλείφθηκαν.` : '.')
          + (written === 0 && firstReason ? ` ${firstReason}` : ''),
      });
      await loadSummary(true);
    } catch (err: any) {
      // 409 «Η τιμή άλλαξε στο efood από τότε που άνοιξες την οθόνη» → ΜΟΝΙΜΟ
      // μήνυμα: ο χειριστής πρέπει να κάνει νέα εισαγωγή, όχι να ξαναπατήσει.
      setNotice(catalogNotice(err, 'Αποτυχία αποθήκευσης αλλαγών'));
    } finally {
      setBusy(false);
    }
  }, [editingAllowed, orgId, platform, loadSummary]);

  const clearEdits = useCallback(async (storeIds: number[] | null) => {
    if (!editingAllowed) return;
    const scope = storeIds && storeIds.length > 0
      ? `των ${storeIds.length} επιλεγμένων καταστημάτων`
      : 'ΟΛΟΥ του οργανισμού';
    if (!window.confirm(`Να διαγραφούν οι πρόχειρες αλλαγές ${scope};`)) return;
    setBusy(true);
    try {
      let deleted = 0;
      if (storeIds && storeIds.length > 0) {
        for (let i = 0; i < storeIds.length; i++) {
          const r = await orgPlatformCatalogAPI.deleteEdits(orgId, platform, { store_id: storeIds[i] });
          deleted += Number(r.deleted) || 0;
        }
      } else {
        const r = await orgPlatformCatalogAPI.deleteEdits(orgId, platform, { all: true });
        deleted = Number(r.deleted) || 0;
      }
      setRows(prev => clearPendingInRows(prev));
      setNotice({ kind: 'ok', text: `Διαγράφηκαν ${deleted} πρόχειρες αλλαγές.` });
      await loadSummary(true);
      await loadGrid(offset, { ...filters, q: debouncedQ });
    } catch (err: any) {
      setNotice(catalogNotice(err, 'Αποτυχία καθαρισμού αλλαγών'));
    } finally {
      setBusy(false);
    }
  }, [editingAllowed, orgId, platform, loadSummary, loadGrid, offset, filters, debouncedQ]);

  /**
   * Στεγνή δοκιμή. Οι `decisions` έρχονται από τα checkbox του πάνελ διδύμων:
   * στέλνονται στον planner και ΜΕΤΑ ελέγχουμε αν το σχέδιο τις τίμησε. Αν ο
   * server αυτής της έκδοσης δεν τις προωθεί ακόμη, το λέμε ρητά — ένα checkbox
   * που δεν αλλάζει τίποτα χωρίς να το πει είναι χειρότερο από καθόλου checkbox.
   */
  const runDryRun = useCallback(async (decisions?: TwinDecisions) => {
    setBusy(true);
    // ΝΕΟ σχέδιο = νέα πρόθεση: η παρακολούθηση της προηγούμενης (τελειωμένης)
    // εκτέλεσης σταματά εδώ, αλλιώς το νέο παράθυρο θα έδειχνε την πρόοδο μιας
    // αποστολής που δεν το αφορά και θα κρατούσε το κουμπί κλειστό.
    setPushRunId(null);
    setPushCanaryStoreId(null);
    try {
      const result = await orgPlatformCatalogAPI.dryRun(
        orgId, platform,
        decisions ? { all: true, twin_decisions: decisions } : { all: true }
      );
      if (!result.run) {
        // Δεν συμβαίνει στη στεγνή δοκιμή (πάντα φτιάχνει σχέδιο), αλλά ο τύπος
        // το επιτρέπει και η οθόνη δεν μαντεύει: λέει τι έγινε.
        setPlan(null);
        setNotice({ kind: 'warn', text: 'Η στεγνή δοκιμή δεν επέστρεψε σχέδιο — δεν υπάρχει τίποτα να σταλεί.' });
        return;
      }
      setPlan({ ...result, run: result.run });
      setTwinDecisionsIgnored(decisions ? !planHonoursTwinDecisions(result, decisions) : false);
      // Σκέτη δοκιμή: η κεφαλίδα του συγχρονισμού ΔΕΝ ισχύει πια. Αν έμενε, το
      // παράθυρο θα απέδιδε στον συγχρονισμό αλλαγές που έφτιαξε το χέρι.
      setSyncHeader(null);
      setPushError(null);
    } catch (err: any) {
      setNotice(catalogNotice(err, 'Αποτυχία στεγνής δοκιμής'));
    } finally {
      setBusy(false);
    }
  }, [orgId, platform]);

  // ── Φ5: αποφάσεις τιμής ─────────────────────────────────────────────────

  /** Η ΙΣΧΥΟΥΣΑ απόφαση: πρόχειρη → αποθηκευμένη → η προεπιλογή «κατάλογος». */
  const draftFor = useCallback((masterKey: string): PriceOverride => {
    const draft = overrideDrafts[masterKey];
    if (draft) return draft;
    const saved = overrides[masterKey];
    if (saved) return saved;
    return { master_key: masterKey, mode: 'catalog', fixed_price: null, note: null };
  }, [overrideDrafts, overrides]);

  const isDirty = useCallback(
    (masterKey: string): boolean => overrideDrafts[masterKey] !== undefined,
    [overrideDrafts]
  );

  /**
   * ΓΙΑΤΙ συγκρίνουμε με την αποθηκευμένη γραμμή αντί να κρατάμε ό,τι αγγίχτηκε:
   * ο χειριστής που αλλάζει «κατάλογος → κράτα → κατάλογος» δεν έκανε καμία
   * αλλαγή. Αν το μετρούσαμε ως αλλαγή, το PUT θα έσβηνε και θα ξανάγραφε
   * γραμμές χωρίς λόγο, και ο μετρητής θα έλεγε ψέματα.
   */
  const handleDraftChange = useCallback((next: PriceOverride) => {
    const key = overrideKey(next);
    if (!key) return;
    const saved = overrides[key] || { master_key: key, mode: 'catalog' as const, fixed_price: null, note: null };
    setOverrideDrafts(prev => {
      const out = { ...prev };
      if (sameOverride(saved, next)) delete out[key];
      else out[key] = next;
      return out;
    });
  }, [overrides]);

  const dirtyOverrides = useMemo(
    () => Object.keys(overrideDrafts).map(k => overrideDrafts[k]),
    [overrideDrafts]
  );

  /**
   * Αποθήκευση αποφάσεων. Γυρίζει `true` ΜΟΝΟ όταν όλα πέρασαν — ο συγχρονισμός
   * το χρησιμοποιεί ως πύλη: αν δεν αποθηκεύτηκαν οι αποφάσεις, ο planner θα
   * διάβαζε τις ΠΑΛΙΕΣ και θα έφτιαχνε ακριβώς τις αλλαγές που μόλις απαγόρευσε
   * ο χειριστής.
   */
  const saveOverrides = useCallback(async (): Promise<boolean> => {
    if (dirtyOverrides.length === 0) return true;
    if (!canManage) {
      setNotice({ kind: 'warn', text: 'Οι αποφάσεις τιμής αποθηκεύονται μόνο από διαχειριστή του οργανισμού.' });
      return false;
    }
    // Ο διακόπτης του οργανισμού (§13.12): σε κατάσταση «ανάγνωσης» το
    // `PUT …/price-overrides` απαντά 403 MODE_READONLY. Χωρίς αυτή την πύλη ο
    // χειριστής έπαιρνε το 403 ΑΦΟΥ είχε πάρει ~20 αποφάσεις, και τις έχανε
    // ολόκληρες στην επόμενη αλλαγή πλατφόρμας.
    if (!editingAllowed) {
      setNotice({
        kind: 'warn',
        text: mode === 'read'
          ? 'Ο επεξεργαστής καταλόγου είναι σε κατάσταση «ανάγνωσης»: οι αποφάσεις τιμής δεν αποθηκεύονται. '
            + 'Ο ιδιοκτήτης του οργανισμού τον γυρίζει σε «επεξεργασία».'
          : 'Δεν επιτρέπεται η αποθήκευση αποφάσεων τιμής με τα τρέχοντα δικαιώματα.',
      });
      return false;
    }
    // «Σταθερή τιμή» σε είδος με μεγέθη ΔΕΝ υπάρχει (§11.7): θα έγραφε το ποσό
    // μόνο στο είδος και θα άφηνε τα μεγέθη στην τιμή της πλατφόρμας. Ο server
    // απαντά 400· το λέμε εδώ, με το όνομα του κανόνα.
    const sizedMaster: Record<string, boolean> = {};
    for (let i = 0; i < priceRows.length; i++) {
      const r = priceRows[i];
      if (r && (r.is_sized === true || r.option_code)) sizedMaster[r.master_key] = true;
    }
    const sizedFixed = dirtyOverrides.filter(o => o.mode === 'fixed' && sizedMaster[o.master_key]);
    if (sizedFixed.length > 0) {
      setNotice({
        kind: 'warn',
        text: `${sizedFixed.length} ${sizedFixed.length === 1 ? 'είδος έχει' : 'είδη έχουν'} μεγέθη: `
          + 'σταθερή τιμή δεν επιτρέπεται εκεί (η τιμή του είδους βγαίνει από τα μεγέθη). '
          + 'Γύρνα τα σε «ακολουθεί τον κατάλογο» ή «κράτα την τιμή efood».',
      });
      return false;
    }
    // «Σταθερή» χωρίς ποσό δεν είναι γραμμή: το CHECK `pcpo_fixed_chk` του 228
    // είναι αμφίδρομο και θα γύριζε 500 αντί για εξήγηση.
    const bad = dirtyOverrides.filter(
      o => o.mode === 'fixed' && (o.fixed_price === null || o.fixed_price === undefined || !isFinite(Number(o.fixed_price)) || Number(o.fixed_price) <= 0)
    );
    if (bad.length > 0) {
      setNotice({
        kind: 'warn',
        text: `${bad.length} ${bad.length === 1 ? 'απόφαση «σταθερή τιμή» δεν έχει' : 'αποφάσεις «σταθερή τιμή» δεν έχουν'} ποσό. `
          + 'Συμπλήρωσέ το ή γύρνα την απόφαση σε «ακολουθεί τον κατάλογο».',
      });
      return false;
    }
    setOverridesSaving(true);
    try {
      const report = await orgPlatformCatalogAPI.putPriceOverrides(orgId, platform, dirtyOverrides);
      setOverrideDrafts({});
      await loadOverrides();
      // ΝΕΕΣ αποφάσεις = άλλο `would_send`/`override_mode` σε κάθε γραμμή: η
      // μνήμη της σύγκρισης αδειάζει, δεν «ανανεώνεται» πάνω σε παλιά δεδομένα.
      await loadPrices(true, true);
      const skipped = report.skipped.length;
      setNotice({
        kind: skipped > 0 ? 'warn' : 'ok',
        text: `Αποθηκεύτηκαν ${report.upserted} αποφάσεις τιμής`
          + (report.deleted > 0 ? ` και επέστρεψαν ${report.deleted} στην προεπιλογή «κατάλογος»` : '')
          + (skipped > 0 ? `. ${skipped} παραλείφθηκαν (άγνωστο κεντρικό είδος).` : '.'),
      });
      return true;
    } catch (err: any) {
      setNotice(catalogNotice(err, 'Αποτυχία αποθήκευσης αποφάσεων τιμής'));
      return false;
    } finally {
      setOverridesSaving(false);
    }
  }, [dirtyOverrides, canManage, editingAllowed, mode, priceRows, orgId, platform, loadOverrides, loadPrices]);

  /**
   * «Συγχρονισμός τιμών»: ΠΡΩΤΑ αποθηκεύονται οι αποφάσεις, ΜΕΤΑ τρέχει ο
   * συγχρονισμός, και το αποτέλεσμα ανοίγει ως ΚΑΝΟΝΙΚΗ στεγνή δοκιμή. Καμία
   * εξερχόμενη κλήση σε αυτό το βήμα — μόνο πρόχειρες αλλαγές στη βάση μας.
   */
  const confirmSync = useCallback(async () => {
    if (!editingAllowed) return;
    setSyncBusy(true);
    setSyncError(null);
    try {
      const saved = await saveOverrides();
      if (!saved) { setSyncBusy(false); return; }
      const result = await orgPlatformCatalogAPI.priceSync(orgId, {
        platform,
        store_ids: priceStoreId === null ? undefined : [priceStoreId],
      });
      setSyncOpen(false);
      // Ο server απαντά ΡΗΤΑ `run: null` όταν καμία τιμή δεν χρειάζεται αλλαγή
      // (η κανονική κατάσταση αμέσως μετά από πετυχημένο συγχρονισμό, και αυτή
      // που ζητά το §13.19.ΙΓ βήμα 2). Τότε ΔΕΝ υπάρχει σχέδιο: δείχνουμε το
      // μήνυμά του. Το `setPlan` εδώ ξεμοντάριζε ολόκληρη την καρτέλα.
      if (!result.run) {
        setPlan(null);
        setSyncHeader(null);
        const created = result.sync ? result.sync.created + result.sync.updated : 0;
        setNotice({
          kind: 'ok',
          text: result.message
            || (created === 0
              ? 'Καμία τιμή δεν χρειάζεται αλλαγή: ο κατάλογος και το efood συμφωνούν σε ό,τι συγκρίθηκε.'
              : 'Ο συγχρονισμός δεν παρήγαγε σχέδιο αποστολής.'),
        });
        await loadSummary(true);
        await loadGrid(offset, { ...filters, q: debouncedQ });
        await loadPrices(true, true);
        return;
      }
      setPlan({ ...result, run: result.run });
      setSyncHeader(result.sync || null);
      setTwinDecisionsIgnored(false);
      setPushError(null);
      await loadSummary(true);
      await loadGrid(offset, { ...filters, q: debouncedQ });
    } catch (err: any) {
      setSyncError(catalogNotice(err, 'Αποτυχία συγχρονισμού τιμών').text);
    } finally {
      setSyncBusy(false);
    }
  }, [
    editingAllowed, saveOverrides, orgId, platform, priceStoreId, loadSummary, loadGrid,
    loadPrices, offset, filters, debouncedQ,
  ]);

  /**
   * Μετά από ΚΑΘΕ εξερχόμενη εγγραφή, ό,τι βλέπει η οθόνη είναι παλιό: σύνοψη,
   * πλέγμα και σύγκριση ξαναδιαβάζονται και η μνήμη τιμών αδειάζει.
   */
  const refreshAfterWrite = useCallback(async () => {
    await loadSummary(true);
    await loadGrid(offset, { ...filters, q: debouncedQ });
    priceCache.current = {};
    if (priceWanted) await loadPrices(true, true);
  }, [loadSummary, loadGrid, offset, filters, debouncedQ, priceWanted, loadPrices]);

  /** Όνομα καταστήματος για μηνύματα — «#13» δεν λέει τίποτα στον χειριστή. */
  const storeNameOf = useCallback((id: number | null): string => {
    if (id === null) return '';
    const list = summary ? summary.stores : [];
    const hit = list.filter(x => x.store_id === id)[0];
    return hit ? hit.store_name : `#${id}`;
  }, [summary]);

  // ── Φ5: μετάπτωση ───────────────────────────────────────────────────────

  const loadMigratePreview = useCallback(async (storeId: number) => {
    setMigratePreviewLoading(true);
    setMigrateError(null);
    try {
      setMigratePreview(await orgPlatformCatalogAPI.migrateDryRun(orgId, { platform, store_id: storeId }));
    } catch (err: any) {
      setMigratePreview(null);
      setMigrateError(isMissingRoute(err)
        ? 'Αυτός ο server δεν έχει ακόμη τη μετάπτωση (φάση 5).'
        : catalogNotice(err, 'Αποτυχία στεγνής δοκιμής μετάπτωσης').text);
    } finally {
      setMigratePreviewLoading(false);
    }
  }, [orgId, platform]);

  const openMigrate = useCallback((storeId: number) => {
    setMigrateStoreId(storeId);
    setMigratePreview(null);
    setMigrateResult(null);
    setMigrateError(null);
    loadMigratePreview(storeId);
  }, [loadMigratePreview]);

  /**
   * Η ΜΗ ΑΝΑΣΤΡΕΨΙΜΗ κλήση.
   *
   * Ό,τι κι αν γυρίσει —ακόμη και σφάλμα δικτύου— ο κατάλογος ΜΠΟΡΕΙ να έχει
   * ήδη αντικατασταθεί στο efood. Γι' αυτό μετά από κάθε έκβαση ξαναφορτώνουμε
   * σύνοψη και πλέγμα: η οθόνη δεν επιτρέπεται να δείχνει το «πριν».
   */
  const confirmMigrate = useCallback(async (confirmText: string) => {
    if (migrateStoreId === null || !migratePreview) return;
    setMigrateRunning(true);
    setMigrateError(null);
    try {
      const result = await orgPlatformCatalogAPI.migrate(orgId, {
        platform,
        store_id: migrateStoreId,
        confirm_hash: migratePreview.confirm_hash,
        confirm_text: confirmText,
      });
      setMigrateResult(result);
      // 202: η μετάπτωση τρέχει ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ (ingest → αναμονή 6-20΄΄ → νέα
      // εισαγωγή → re-key). Δεν χωρά σε ένα αίτημα HTTP· το παράθυρο μένει
      // ανοιχτό και ρωτά `GET …/runs/:id` κάθε 3΄΄.
      if (!isTerminalRunStatus(result.status)) {
        setMigrateRunId(result.run_id);
        setMigrateBgStoreId(migrateStoreId);
        return;
      }
      if (result.status !== 'done') {
        setMigrateError(result.error || 'Η μετάπτωση δεν ολοκληρώθηκε — δες τα βήματα παρακάτω.');
      }
      setMigrateRunning(false);
      await refreshAfterWrite();
    } catch (err: any) {
      setMigrateRunning(false);
      setMigrateError(isTransportError(err)
        ? TRANSPORT_TEXT
        : catalogNotice(err, 'Αποτυχία μετάπτωσης').text
          + ' Μην ξαναπατήσεις πριν ελέγξεις το ιστορικό: η αποστολή μπορεί να έφυγε.');
      await refreshAfterWrite();
    }
  }, [migrateStoreId, migratePreview, orgId, platform, refreshAfterWrite]);

  /**
   * Η μετάπτωση τελείωσε στο παρασκήνιο. ΚΑΘΕ έκβαση εκτός από `done` κρατά το
   * παράθυρο με κόκκινο μήνυμα: το ingest μπορεί να έχει ήδη περάσει και να
   * απέτυχε κάποιο επόμενο βήμα — δεύτερο ingest ΔΕΝ γίνεται αυτόματα.
   */
  const handleMigrateFinished = useCallback((detail: RunDetail | null) => {
    setMigrateRunning(false);
    setMigrateBgStoreId(null);
    const status = detail ? detail.status : null;
    const job = detail && detail.jobs && detail.jobs.length > 0 ? detail.jobs[0] : null;

    /**
     * Η ΑΝΑΦΟΡΑ ΤΗΣ ΜΟΝΑΔΙΚΗΣ ΜΗ ΑΝΑΣΤΡΕΨΙΜΗΣ ΕΝΕΡΓΕΙΑΣ.
     *
     * Το `migrateResult` κρατούσε το στέλεχος του 202 (`status: 'running'`) και
     * τίποτα άλλο — άρα το πράσινο «Ολοκληρώθηκε» και η αναφορά re-key του
     * MigrateModal ήταν νεκρός κώδικας: μετά από ΕΠΙΤΥΧΗΜΕΝΗ μετάπτωση κανείς
     * δεν έβλεπε πόσοι σύνδεσμοι/δίδυμα/πρόχειρα μεταφέρθηκαν και πόσα έμειναν
     * ορφανά. Τα δεδομένα ταξιδεύουν ΗΔΗ στο `GET …/runs/:id` (`jobs[0].rekey_report`):
     * τα γράφουμε πάνω στο στέλεχος, χωρίς δεύτερο αίτημα.
     */
    setMigrateResult(prev => (prev ? {
      ...prev,
      status: (status || prev.status) as MigrationResult['status'],
      job_id: job && job.id !== undefined && job.id !== null ? job.id : prev.job_id,
      rekey: job && job.rekey_report ? job.rekey_report : (prev.rekey || null),
      error: (job && job.error)
        || (detail && detail.halted_reason ? detail.halted_reason : null)
        || prev.error
        || null,
    } : prev));

    if (status !== 'done') {
      setMigrateError(
        `Η μετάπτωση τελείωσε με κατάσταση «${status || 'άγνωστη'}»`
        + (detail && detail.halted_reason ? ` (${detail.halted_reason})` : '')
        + (job && job.error ? `: ${job.error}` : '.')
        + ' Το efood μπορεί να έχει ΗΔΗ αντικατασταθεί — έλεγξε το «Ιστορικό» πριν από οτιδήποτε άλλο.'
      );
    }
    refreshAfterWrite();
  }, [refreshAfterWrite]);

  const migrateProgress = useRunWatch(orgId, migrateRunId, handleMigrateFinished);

  /**
   * Η δημοσκόπηση ΔΕΝ τερματίζει σε σφάλμα μεταφοράς — σωστά, η εκτέλεση
   * συνεχίζει. Αλλά τότε το `migrateRunning` δεν σβήνει ποτέ: χωρίς αυτό το φλαγκάκι
   * το παράθυρο είναι modal χωρίς έξοδο.
   */
  const migrateStuck = runWatchStuck(migrateProgress);

  /**
   * Έξοδος από το παράθυρο — ΠΟΤΕ ακύρωση της εκτέλεσης.
   *
   * Η παρακολούθηση ΣΥΝΕΧΙΖΕΙ όσο η εκτέλεση δεν έχει τελειώσει: με το νέο
   * (και σωστό) ξεκλείδωμα του κλεισίματος, ένα `setMigrateRunId(null)` θα άφηνε το
   * πλακίδιο ενεργό πάνω σε ΜΗ ΑΝΑΣΤΡΕΨΙΜΗ κλήση που ακόμη γράφει στο efood.
   */
  const closeMigrate = useCallback(() => {
    setMigrateStoreId(null);
    setMigratePreview(null);
    setMigrateResult(null);
    setMigrateError(null);
    setMigrateRunning(false);
    setMigrateRunId(prev => (migrateBgStoreId === null ? null : prev));
  }, [migrateBgStoreId]);

  // ── Φ5: αποστολή ────────────────────────────────────────────────────────

  /**
   * Η πραγματική αποστολή των deltas (§13.19.ΣΤ).
   *
   * Το `confirm_hash` είναι ΤΟΥ ΣΧΕΔΙΟΥ που είδε ο άνθρωπος. Το 409/403 της
   * πύλης μένει ΜΕΣΑ στο παράθυρο, δίπλα στο κουμπί που το προκάλεσε — ένα
   * toast 2,5΄΄ θα χανόταν πριν διαβαστεί, και η επόμενη κίνηση του χειριστή θα
   * ήταν να ξαναπατήσει το ίδιο κουμπί.
   */
  const doPush = useCallback(async () => {
    if (!plan || !canManage) return;
    setPushing(true);
    setPushError(null);
    setPushRunId(null);
    try {
      const result = await orgPlatformCatalogAPI.push(orgId, platform, {
        // ΠΑΝΤΑ ολόκληρο το ανοιχτό σύνολο. Το σχέδιο —είτε από το καλάθι είτε
        // από τον συγχρονισμό τιμών— χτίζεται από ΟΛΕΣ τις ανοιχτές πρόχειρες
        // αλλαγές του οργανισμού και το `confirm_hash` περιγράφει ακριβώς αυτό.
        // Στέλνοντας υποσύνολο (π.χ. μόνο τα edit_ids του συγχρονισμού), το
        // `buildRun` ξαναχτίζει ΑΛΛΟ σχέδιο και απαντά μόνιμο 409 confirm_stale.
        all: true,
        confirm_hash: plan.run.confirm_hash,
      });
      // Το καναρίνι το λέει ο server· αν δεν το πει, το ξέρει το ΙΔΙΟ το σχέδιο
      // (η εργασία με `canary: true` φεύγει πρώτη). Είναι το κατάστημα που
      // παρακολουθεί ο χειριστής 24 ώρες — δεν επιτρέπεται να μείνει άγνωστο.
      const canary = (result.canary_store_id === null || result.canary_store_id === undefined)
        ? canaryOf(plan)
        : result.canary_store_id;
      setPushCanaryStoreId(canary);
      setPushRunId(result.run_id);
      setNotice({
        kind: 'ok',
        text: `Η αποστολή ξεκίνησε (εκτέλεση #${result.run_id}`
          + (canary !== null ? `, καναρίνι: ${storeNameOf(canary)}` : '')
          + '). Η πρόοδος ενημερώνεται μέσα στο παράθυρο κάθε 3΄΄ και μένει στο «Ιστορικό» — '
          + 'η εκτέλεση ΔΕΝ σταματά αν κλείσεις την οθόνη.',
      });
      await loadRuns();
    } catch (err: any) {
      // ΜΟΝΟ εδώ σταματά το «στέλνει»: αν πήραμε run_id, το σταματά η
      // παρακολούθηση όταν η εκτέλεση φτάσει σε τελική κατάσταση.
      setPushing(false);
      setPushError(pushFailureText(err));
    }
  }, [plan, canManage, orgId, platform, loadRuns, storeNameOf]);

  /** Η αποστολή τελείωσε στο παρασκήνιο — ό,τι κι αν έγινε, το λέμε ολόκληρο. */
  const handlePushFinished = useCallback((detail: RunDetail | null) => {
    setPushing(false);
    const status = detail ? detail.status : null;
    const canaryText = pushCanaryStoreId === null ? '' : ` Καναρίνι: ${storeNameOf(pushCanaryStoreId)}.`;
    setNotice(status === 'done'
      ? {
        kind: 'ok',
        text: `Η αποστολή${detail ? ` #${detail.id}` : ''} ολοκληρώθηκε.${canaryText}`
          + ' Δες το «Ιστορικό» για τις κλήσεις ανά κατάστημα.',
      }
      : {
        kind: 'warn',
        text: `Η αποστολή${detail ? ` #${detail.id}` : ''} τελείωσε με κατάσταση «${status || 'άγνωστη'}»`
          + (detail && detail.halted_reason ? ` (${detail.halted_reason})` : '')
          + `.${canaryText} Δες το «Ιστορικό»: ό,τι έχει ήδη σταλεί ΔΕΝ αναιρείται μόνο του.`,
      });
    loadRuns();
    refreshAfterWrite();
  }, [pushCanaryStoreId, storeNameOf, loadRuns, refreshAfterWrite]);

  const pushProgress = useRunWatch(orgId, pushRunId, handlePushFinished);

  // ── Χειριστές του πλέγματος ─────────────────────────────────────────────

  /**
   * ΠΡΟΣΟΧΗ στο `old_value` (§13.6.1): φεύγει ΠΑΝΤΑ η τιμή ΤΗΣ ΠΛΑΤΦΟΡΜΑΣ
   * (`platformPrice`), ποτέ η εκκρεμής. Το backend τη συγκρίνει με την
   * αποθηκευμένη γραμμή — αν στέλναμε το πρόχειρο, η δεύτερη διόρθωση του ίδιου
   * κελιού («έγραψα 1,70, ήθελα 1,80») θα γύριζε 409 «η τιμή άλλαξε στο efood»
   * και δεν θα διορθωνόταν ούτε με νέα εισαγωγή. Το `shownPrice` μένει για την
   * οθόνη και για τον έλεγχο «καμία αλλαγή».
   */
  const handleSetPrice = useCallback((row: GridRow, storeId: number, cell: GridCell, price: number) => {
    submitChanges([{
      store_id: storeId,
      entity_kind: 'item',
      item_code: cell.item_code,
      master_key: row.master_key,
      field: 'price',
      old_value: platformPrice(cell),
      new_value: price,
      apply_to_twins: defaultApplyToTwins(cell, 'price'),
    }]);
  }, [submitChanges]);

  const handleSetAvailability = useCallback((row: GridRow, storeId: number, cell: GridCell, value: boolean) => {
    submitChanges([{
      store_id: storeId,
      entity_kind: 'item',
      item_code: cell.item_code,
      master_key: row.master_key,
      field: 'is_available',
      old_value: platformAvailable(cell),
      new_value: value,
      // Η διαθεσιμότητα παρασύρει δίδυμα ΑΚΡΙΒΩΣ όπως η τιμή (§13.3.3): αλλιώς
      // το κρυφό είδος της προσφοράς μένει ανοιχτό σε κάτι που τελείωσε.
      apply_to_twins: defaultApplyToTwins(cell, 'is_available'),
    }]);
  }, [submitChanges]);

  /** «Τιμή (όλα)»: ΜΙΑ γραμμή edit ανά κατάστημα που όντως διαφέρει (§13.5). */
  const handleSetPriceAll = useCallback((row: GridRow, price: number) => {
    const changes: EditChange[] = [];
    for (let i = 0; i < stores.length; i++) {
      const storeId = stores[i].store_id;
      const cell = row.cells ? row.cells[String(storeId)] : undefined;
      if (!cellIsEditable(cell) || !cell) continue;
      // Ο έλεγχος «διαφέρει;» γίνεται πάνω σε ΑΥΤΟ που βλέπει ο χειριστής, αλλά
      // το old_value που φεύγει είναι πάντα η τιμή της πλατφόρμας.
      const before = shownPrice(cell);
      if (before !== null && Math.abs(before - price) < 0.0001) continue;
      changes.push({
        store_id: storeId,
        entity_kind: 'item',
        item_code: cell.item_code,
        master_key: row.master_key,
        field: 'price',
        old_value: platformPrice(cell),
        new_value: price,
        apply_to_twins: defaultApplyToTwins(cell, 'price'),
      });
    }
    if (changes.length === 0) {
      setNotice({ kind: 'ok', text: 'Όλα τα καταστήματα έχουν ήδη αυτή την τιμή — καμία αλλαγή.' });
      return;
    }
    submitChanges(changes);
  }, [stores, submitChanges]);

  const handleSetAvailabilityAll = useCallback((row: GridRow, value: boolean) => {
    const changes: EditChange[] = [];
    for (let i = 0; i < stores.length; i++) {
      const storeId = stores[i].store_id;
      const cell = row.cells ? row.cells[String(storeId)] : undefined;
      if (!cellIsEditable(cell) || !cell) continue;
      if (shownAvailable(cell) === value) continue;
      changes.push({
        store_id: storeId,
        entity_kind: 'item',
        item_code: cell.item_code,
        master_key: row.master_key,
        field: 'is_available',
        old_value: platformAvailable(cell),
        new_value: value,
        apply_to_twins: defaultApplyToTwins(cell, 'is_available'),
      });
    }
    if (changes.length === 0) {
      setNotice({ kind: 'ok', text: 'Καμία αλλαγή: όλα τα καταστήματα είναι ήδη έτσι.' });
      return;
    }
    submitChanges(changes);
  }, [stores, submitChanges]);

  /**
   * «Να μην υπάρχει στο efood» (§13.19.Γ) — ρίσκο E.
   *
   * ΔΕΝ σβήνει τίποτα τώρα και ΔΕΝ αγγίζει τη γραμμή αντιστοίχισης ονόματος:
   * φτιάχνει πρόχειρη αλλαγή `field='delete'` σε κάθε κατάστημα όπου το είδος
   * υπάρχει. Πριν τη μετάπτωση το είδος απλώς δεν μπαίνει στο payload· μετά
   * φεύγει με `DELETE /catalog`. Η επαναφορά είναι «καθάρισε το πρόχειρο», όσο
   * δεν έχει σταλεί.
   */
  const handleDeleteRow = useCallback((row: GridRow) => {
    const changes: EditChange[] = [];
    let already = 0;
    for (let i = 0; i < stores.length; i++) {
      const storeId = stores[i].store_id;
      const cell = row.cells ? row.cells[String(storeId)] : undefined;
      if (!cellIsEditable(cell) || !cell) continue;
      if (hasPendingDelete(cell)) { already += 1; continue; }
      changes.push({
        store_id: storeId,
        entity_kind: 'item',
        item_code: cell.item_code,
        master_key: row.master_key,
        field: 'delete',
        old_value: false,
        new_value: true,
        // Τα δίδυμα τιμής δένουν ΤΙΜΕΣ, όχι ύπαρξη: το κρυφό είδος μιας
        // προσφοράς δεν διαγράφεται επειδή διαγράφηκε το κανονικό.
        apply_to_twins: false,
      });
    }
    if (changes.length === 0) {
      setNotice({
        kind: 'ok',
        text: already > 0
          ? `Το «${row.display_name}» είναι ήδη σημειωμένο για διαγραφή σε ${already} ${already === 1 ? 'κατάστημα' : 'καταστήματα'}.`
          : 'Δεν υπάρχει κατάστημα όπου να μπορεί να σημειωθεί για διαγραφή αυτό το είδος.',
      });
      return;
    }
    if (!window.confirm(
      `Να σημειωθεί το «${row.display_name}» ώστε να ΜΗΝ υπάρχει στο efood σε ${changes.length} `
      + `${changes.length === 1 ? 'κατάστημα' : 'καταστήματα'};\n\n`
      + 'Δεν σβήνεται τίποτα τώρα: δημιουργείται πρόχειρη αλλαγή που φεύγει με την αποστολή ή τη '
      + 'μετάπτωση. Η αντιστοίχιση ονόματος δεν πειράζεται.'
    )) return;
    submitChanges(changes);
  }, [stores, submitChanges]);

  const toggleRow = useCallback((masterKey: string) => {
    setSelectedRows(prev => {
      const next = { ...prev };
      if (next[masterKey]) delete next[masterKey];
      else next[masterKey] = true;
      return next;
    });
  }, []);

  const toggleStore = useCallback((storeId: number) => {
    setSelectedStores(prev => {
      const next = { ...prev };
      const key = String(storeId);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }, []);

  const toggleAllRows = useCallback((value: boolean) => {
    if (!value) { setSelectedRows({}); return; }
    const next: Record<string, boolean> = {};
    rowsRef.current.forEach(r => { next[r.master_key] = true; });
    setSelectedRows(next);
  }, []);

  const toggleCategory = useCallback((label: string) => {
    setCollapsed(prev => {
      const next = { ...prev };
      if (next[label]) delete next[label];
      else next[label] = true;
      return next;
    });
  }, []);

  const downloadWorklist = useCallback(async () => {
    if (offersStore === null) return;
    setWorklistBusy(true);
    try {
      const blob = await orgPlatformCatalogAPI.worklistCsv(orgId, platform, offersStore);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `worklist-${platform}-${offersStore}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setNotice(catalogNotice(err, 'Αποτυχία λήψης λίστας εργασίας'));
    } finally {
      setWorklistBusy(false);
    }
  }, [offersStore, orgId, platform]);

  const selectRun = useCallback(async (id: number) => {
    if (runId === id) { setRunId(null); setRunDetail(null); return; }
    setRunId(id);
    setRunDetailLoading(true);
    try {
      setRunDetail(await orgPlatformCatalogAPI.getRun(orgId, id));
    } catch (err: any) {
      setRunDetail(null);
      setNotice(catalogNotice(err, 'Αποτυχία φόρτωσης εκτέλεσης'));
    } finally {
      setRunDetailLoading(false);
    }
  }, [runId, orgId]);

  const cancelRun = useCallback(async (id: number) => {
    if (!window.confirm('Να σταματήσει η εκτέλεση; Ό,τι έχει ήδη σταλεί στο efood ΔΕΝ αναιρείται.')) return;
    try {
      const res = await orgPlatformCatalogAPI.cancelRun(orgId, id);
      // Η ακύρωση ΔΕΝ σταματά την κλήση που ήδη τρέχει: το λέει ο server και το
      // μεταφέρουμε αυτούσιο, αλλιώς ο χειριστής νομίζει ότι σταμάτησαν όλα.
      setNotice({
        kind: 'ok',
        text: res.message
          || `Ζητήθηκε ακύρωση της εκτέλεσης #${id}: η τρέχουσα κλήση ολοκληρώνεται, τα επόμενα καταστήματα ακυρώνονται.`,
      });
      await loadRuns();
    } catch (err: any) {
      setNotice(catalogNotice(err, 'Αποτυχία ακύρωσης εκτέλεσης'));
    }
  }, [orgId, loadRuns]);

  // ── Παράγωγα ────────────────────────────────────────────────────────────

  const categories = knownCategories;

  // Άλλη πλατφόρμα = άλλος κατάλογος: η σωρευτική λίστα κατηγοριών μηδενίζεται,
  // αλλιώς ο επιλογέας θα πρόσφερε κατηγορίες που δεν υπάρχουν εδώ.
  useEffect(() => { setKnownCategories([]); }, [platform]);

  const selectionCount = Object.keys(selectedRows).length;
  const openEdits = summary ? summary.totals.open_edits : 0;
  const conflicts = summary ? summary.totals.conflicts : 0;
  const importableStores = useMemo(
    () => (summary ? summary.stores.filter(s => s.has_credentials && s.last_import_at !== null) : []),
    [summary]
  );

  useEffect(() => {
    if (offersStore === null && importableStores.length > 0) setOffersStore(importableStores[0].store_id);
  }, [importableStores, offersStore]);

  /**
   * Το ευρετήριο σύγκρισης — χτίζεται ΜΙΑ φορά ανά φόρτωση, όχι ανά κελί.
   *
   * `complete` μόνο όταν ζητήθηκε ΟΛΗ η σύγκριση ΚΑΙ ο server δεν την έκοψε:
   * αλλιώς η απουσία γραμμής σημαίνει «δεν ρωτήθηκε», όχι «συμφωνούν» — και το
   * πλέγμα γράφει «—». Οι γραμμές χωρίς διαφορά (χωρίς αντιστοίχιση, εκτός
   * καταστήματος, είδη προσφοράς) μπαίνουν ΤΕΛΕΥΤΑΙΕΣ στην ταξινόμηση και είναι
   * ακριβώς αυτές που κόβονται πρώτες — δηλαδή αυτές για τις οποίες ένα «ίδια»
   * θα ήταν ψέμα.
   */
  /**
   * Το `complete` σημαίνει «ΟΛΟΣ ο οργανισμός, όλες οι γραμμές» — γιατί μόνο
   * τότε επιτρέπεται στο πλέγμα να γράψει «ίδια» με tooltip «καμία διαφορά … σε
   * κανένα κατάστημα». Το `priceStoreId` ΕΙΝΑΙ όρος: το πλέγμα δείχνει ΟΛΑ τα
   * καταστήματα, άρα σύγκριση ΕΝΟΣ καταστήματος δεν λέει ΤΙΠΟΤΑ για τα υπόλοιπα.
   */
  const priceIndex = useMemo(
    () => buildPriceIndex(
      priceRows, priceIndexComplete(priceOnlyMismatch, priceTruncated, priceStoreId !== null)
    ),
    [priceRows, priceOnlyMismatch, priceTruncated, priceStoreId]
  );

  const allStores = useMemo(() => (summary ? summary.stores : []), [summary]);

  const migrateStore = useMemo(
    () => allStores.filter(s => s.store_id === migrateStoreId)[0] || null,
    [allStores, migrateStoreId]
  );

  const priceStoreName = useMemo(() => {
    if (priceStoreId === null) return null;
    const s = allStores.filter(x => x.store_id === priceStoreId)[0];
    return s ? s.store_name : `#${priceStoreId}`;
  }, [allStores, priceStoreId]);

  /** Πύλες 1-3 του §13.8.1, όπως τις υπολογίζει ο ΙΔΙΟΣ ο server. */
  const serverCanPush = !!summary && (summary.server_can_push === true || summary.push_available === true);
  /** Ο ιδιοκτήτης· όσο δεν τον ξεχωρίζει ο ενορχηστρωτής, το λέει το 403. */
  const ownerAllowed = canOwn === undefined ? canManage : canOwn;
  /**
   * Πόσα κελιά είναι σημειωμένα για διαγραφή ΣΤΙΣ ΦΟΡΤΩΜΕΝΕΣ γραμμές.
   *
   * Δεν υπάρχει σύνολο στη σύνοψη (το `open_edits` τα μετρά όλα μαζί), και δεν
   * το εφευρίσκουμε: το καλάθι λέει ρητά «στις ορατές» όταν η σελίδα δεν είναι
   * ολόκληρος ο κατάλογος.
   */
  const pendingDeletes = useMemo(() => {
    let n = 0;
    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].cells || {};
      const keys = Object.keys(cells);
      for (let j = 0; j < keys.length; j++) if (hasPendingDelete(cells[keys[j]])) n += 1;
    }
    return n;
  }, [rows]);

  const priceMismatchCount = useMemo(() => {
    const keys = Object.keys(priceIndex.byMaster);
    let n = 0;
    for (let i = 0; i < keys.length; i++) if (priceIndex.byMaster[keys[i]].delta !== null) n += 1;
    return n;
  }, [priceIndex]);

  // ── Ο διακόπτης χαρακτηριστικού ─────────────────────────────────────────

  if (mode === 'off') {
    // Χωρίς CatalogHeader (δεν έχει τίποτα να δείξει): η κεφαλίδα της σελίδας είναι της ListPage.
    return (
      <ListPage eyebrow="Πλατφόρμες" title="Κατάλογος πλατφορμών" testId="org-platform-catalog-off">
        <Banner tone="info" icon={Lock} role="status">
          Ο επεξεργαστής καταλόγου πλατφορμών δεν είναι ενεργός για αυτόν τον οργανισμό. Ενεργοποιείται
          κεντρικά σε κατάσταση «ανάγνωση» (εισαγωγή + πλέγμα) ή «επεξεργασία» (με πρόχειρες αλλαγές).
        </Banner>
      </ListPage>
    );
  }

  // Υπο-καρτέλες: το πλέγμα είναι το προϊόν, τα άλλα είναι υποστηρικτικά. Ο αριθμός στις «Τιμές»
  // μετράει ΚΕΝΤΡΙΚΑ ΕΙΔΗ με διαφορά, όχι γραμμές: οι 140 γραμμές «+0,10» είναι ~17 αποφάσεις, και
  // ο χειριστής πρέπει να δει το μέγεθος της δουλειάς του, όχι του πίνακα.
  const tabs: Array<SubTab<View>> = [
    { id: 'grid', label: 'Πλέγμα' },
    {
      id: 'prices',
      label: 'Τιμές',
      badge: priceMismatchCount > 0 ? (
        <span
          title={`${priceMismatchCount} κεντρικά είδη με διαφορά τιμής από τον κατάλογο`}
          className="rounded-full bg-amber-500/20 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800 dark:text-amber-200"
        >
          {priceMismatchCount}
        </span>
      ) : null,
    },
    { id: 'offers', label: 'Προσφορές & δίδυμα' },
    { id: 'history', label: 'Ιστορικό' },
  ];

  return (
    <CatalogSheet>
      <CatalogHeader
        platform={platform}
        onPlatformChange={setPlatform}
        summary={summary}
        loading={summaryLoading || gridLoading}
        canImport={canEdit}
        importing={importing}
        progress={progress}
        hasOpenEdits={openEdits > 0}
        onImportAll={importAll}
        onImportStore={storeId => importStores([storeId])}
        onRefresh={() => { loadSummary(false); loadGrid(offset, { ...filters, q: debouncedQ }); }}
      />

      {notice && (
        <Banner
          tone={notice.kind === 'err' ? 'danger' : notice.kind}
          role={notice.kind === 'err' ? 'alert' : 'status'}
          onDismiss={() => setNotice(null)}
          dismissLabel="Κλείσιμο μηνύματος"
          testId="platform-catalog-notice"
        >
          {notice.text}
        </Banner>
      )}

      {/* Οι πύλες αποστολής είναι δουλειά του org_owner (§13.12) και δεν αλλάζουν
          από αυτή την οθόνη — αλλά ο διαχειριστής πρέπει να βλέπει πού βρίσκονται,
          αλλιώς νομίζει ότι φταίει η οθόνη όταν η αποστολή μείνει κλειστή. */}
      {canManage && summary && summary.stores.length > 0 && (
        <p className="text-xs leading-5 text-t3">
          Άδεια εγγραφής: <strong className="font-semibold text-t2">{summary.stores.filter(s => s.write_enabled).length}</strong>/
          {summary.stores.length} καταστήματα · Διευθυνσιοδότηση ορισμένη:{' '}
          <strong className="font-semibold text-t2">{summary.stores.filter(s => s.addressing !== 'unknown').length}</strong>/
          {summary.stores.length}. Και τα δύο ορίζονται από τον ιδιοκτήτη του οργανισμού, μετά από
          επιβεβαίωση του efood — μέχρι τότε η αποστολή μένει κλειδωμένη.
        </p>
      )}

      {mode === 'read' && (
        <Banner tone="info" icon={Lock} role="note">
          Κατάσταση <strong>ανάγνωσης</strong>: εισαγωγή, πλέγμα, δίδυμα και λίστα εργασίας δουλεύουν,
          αλλά δεν αποθηκεύονται αλλαγές. Είναι η χρήσιμη κατάσταση όσο δεν έχει επιβεβαιωθεί πώς
          αναγνωρίζει το efood τα υπάρχοντα είδη.
        </Banner>
      )}

      {/* Σε δικό του block: το SubTabs είναι inline-flex και, σκέτο μέσα στο space-y, θα έπαιρνε
          κενό γραμμής κειμένου από κάτω. */}
      <div>
        <SubTabs<View>
          label="Ενότητες καταλόγου πλατφόρμας"
          tabs={tabs}
          value={view}
          onChange={setView}
          testId="platform-catalog-tabs"
        />
      </div>

      {/* Πλακίδια μετάπτωσης: πάνω από κάθε υπο-καρτέλα, γιατί ΟΡΙΖΟΥΝ τι
          επιτρέπεται παρακάτω — πριν τη μετάπτωση καμία αποστολή δεν μπορεί καν
          να απευθυνθεί στα είδη του καταστήματος (§13.18). */}
      {(view === 'grid' || view === 'prices') && (
        <MigrationTiles
          stores={allStores}
          canMigrate={ownerAllowed && editingAllowed}
          serverCanPush={serverCanPush}
          busyStoreId={migrateRunning ? migrateStoreId : migrateBgStoreId}
          onMigrate={openMigrate}
        />
      )}

      {view === 'grid' && (
        <>
          {/* Η σύγκριση με τον κατάλογο ΔΕΝ τρέχει μόνη της: για 20 καταστήματα
              είναι ~180 ερωτήματα στη βάση. Όσο δεν έχει ζητηθεί, η στήλη
              «Κατάλογος» γράφει «—» και εξηγεί γιατί — ποτέ «ίδια». */}
          {!priceWanted ? (
            <p className="text-sm text-t3">
              Η στήλη «Κατάλογος (delivery)» είναι κενή: η σύγκριση διαβάζει όλα τα καταστήματα και δεν
              φορτώνεται από μόνη της.{' '}
              <button type="button" onClick={requestPrices} className={LINK_BTN}>
                Φόρτωσε τη σύγκριση τιμών
              </button>
            </p>
          ) : (priceUnavailable ? (
            <Banner tone="warn" role="status">{priceUnavailable}</Banner>
          ) : null)}

          <CatalogFilters
            filters={filters}
            categories={categories}
            loading={gridLoading}
            total={total}
            shown={rows.length}
            onChange={setFilters}
            onReload={() => loadGrid(offset, { ...filters, q: debouncedQ })}
          />

          {selectionCount > 0 && (
            <BulkActionsBar
              rows={rows}
              stores={stores}
              selectedRows={selectedRows}
              selectedStores={selectedStores}
              canEdit={editingAllowed}
              busy={busy}
              onApply={submitChanges}
              onClearSelection={() => { setSelectedRows({}); setSelectedStores({}); }}
              onClearEdits={clearEdits}
            />
          )}

          <CatalogGrid
            rows={rows}
            stores={stores}
            loading={gridLoading}
            canEdit={editingAllowed}
            height={gridHeight}
            collapsed={collapsed}
            onToggleCategory={toggleCategory}
            selectedRows={selectedRows}
            selectedStores={selectedStores}
            onToggleRow={toggleRow}
            onToggleStore={toggleStore}
            onToggleAllRows={toggleAllRows}
            onSetPrice={handleSetPrice}
            onSetAvailability={handleSetAvailability}
            onSetPriceAll={handleSetPriceAll}
            onSetAvailabilityAll={handleSetAvailabilityAll}
            // `null` = ΔΕΝ ρωτήθηκε/δεν ήρθε ακόμη: το πλέγμα γράφει «—» με
            // την εξήγηση, αντί να κρίνει πάνω σε άδειο ευρετήριο.
            priceIndex={(!priceWanted || (priceLoading && priceIndex.rows === 0)) ? null : priceIndex}
            canDelete={editingAllowed}
            onDeleteRow={handleDeleteRow}
          />

          {total > GRID_PAGE && (
            <nav className="flex items-center justify-between gap-3 text-sm text-t3" aria-label="Σελίδες πλέγματος">
              <button
                type="button"
                disabled={offset === 0 || gridLoading}
                onClick={() => {
                  const next = Math.max(0, offset - GRID_PAGE);
                  setOffset(next);
                  loadGrid(next, { ...filters, q: debouncedQ });
                }}
                className={GHOST_BTN}
              >
                Προηγούμενες
              </button>
              <span className="tabular-nums">{offset + 1}–{Math.min(offset + rows.length, total)} από {total}</span>
              <button
                type="button"
                disabled={offset + GRID_PAGE >= total || gridLoading}
                onClick={() => {
                  const next = offset + GRID_PAGE;
                  setOffset(next);
                  loadGrid(next, { ...filters, q: debouncedQ });
                }}
                className={GHOST_BTN}
              >
                Επόμενες
              </button>
            </nav>
          )}

          <p className="text-xs leading-5 text-t4">
            Υπόμνημα: <strong>—</strong> λείπει από το κατάστημα · κίτρινο τρίγωνο = διαφορετική δομή ·
            μωβ κουκκίδα = είδος κατηγορίας προσφοράς · <strong>×2</strong> = δίδυμο τιμής (κίτρινο όταν οι
            τιμές διαφέρουν ήδη) · πορτοκαλί τιμή = εκκρεμής αλλαγή · κόκκινο κελί = σύγκρουση, θέλει νέα εισαγωγή ·
            πορτοκαλί <strong>+0,10</strong> = διαφορά από τον κατάλογο (delivery) που θα σταλεί με τον
            συγχρονισμό · μπλε = διαφορά με απόφαση «κράτα την τιμή efood», δεν στέλνεται ποτέ ·
            διαγραμμένη τιμή σε κόκκινο πλαίσιο = σημειωμένο για διαγραφή από το efood.
          </p>
        </>
      )}

      {view === 'prices' && (
        <PricesPanel
          stores={allStores}
          storeId={priceStoreId}
          onStoreChange={setPriceStoreId}
          onlyMismatch={priceOnlyMismatch}
          onOnlyMismatchChange={setPriceOnlyMismatch}
          q={priceQ}
          onQChange={setPriceQ}
          index={priceIndex}
          summary={priceSummary}
          total={priceTotal}
          loading={priceLoading}
          unavailable={priceUnavailable}
          requested={priceWanted}
          // Η απόφαση τιμής ΓΡΑΦΕΤΑΙ (PUT …/price-overrides): θέλει και ρόλο και
          // ανοιχτό διακόπτη. Με `canManage` σκέτο, η οθόνη άνοιγε χειριστήριο
          // που ο server απαντά 403 MODE_READONLY — και οι αποφάσεις χάνονταν.
          canDecide={canManage && editingAllowed}
          canSync={editingAllowed}
          draftFor={draftFor}
          isDirty={isDirty}
          onDraftChange={handleDraftChange}
          dirtyCount={dirtyOverrides.length}
          saving={overridesSaving}
          onSave={() => { saveOverrides(); }}
          onDiscardDrafts={() => setOverrideDrafts({})}
          onReload={requestPrices}
          onOpenSync={() => { setSyncError(null); setSyncOpen(true); }}
        />
      )}

      {view === 'offers' && (
        <OffersPanel
          stores={importableStores}
          storeId={offersStore}
          onStoreChange={setOffersStore}
          data={twins}
          loading={twinsLoading}
          worklistBusy={worklistBusy}
          onDownloadWorklist={downloadWorklist}
        />
      )}

      {view === 'history' && (
        <RunHistory
          runs={runs}
          loading={runsLoading}
          selectedId={runId}
          detail={runDetail}
          detailLoading={runDetailLoading}
          onSelect={selectRun}
          canCancel={canManage}
          onCancel={cancelRun}
        />
      )}

      <DraftBasket
        openEdits={openEdits}
        conflicts={conflicts}
        stores={allStores}
        canEdit={editingAllowed}
        busy={busy}
        pendingDeletes={pendingDeletes}
        deletesPartial={total > rows.length}
        // ΧΩΡΙΣ όρισμα: σκέτο `runDryRun` θα έπαιρνε το DOM event ως πρώτο
        // όρισμα και θα το έστελνε ως «αποφάσεις διδύμων».
        onDryRun={() => runDryRun()}
        onClearAll={() => clearEdits(null)}
      />

      {plan && (
        // key = confirm_hash: ΝΕΟ σχέδιο σημαίνει νέο παράθυρο. Χωρίς αυτό, οι
        // επιβεβαιώσεις φραγών και οι αποφάσεις διδύμων επιβίωναν πάνω από ένα
        // σχέδιο που μπορεί να έχει άλλες φραγές (§13.6.2 — η επιβεβαίωση είναι
        // η μόνη ανθρώπινη πύλη πριν από την αποστολή της Φ5).
        <DryRunModal
          key={plan.run.confirm_hash}
          plan={plan}
          busy={busy}
          twinDecisionsIgnored={twinDecisionsIgnored}
          onClose={() => {
            setPlan(null);
            setTwinDecisionsIgnored(false);
            setSyncHeader(null);
            setPushError(null);
            // Κλείσιμο ΠΑΝΩ σε εκτέλεση: η εκτέλεση συνεχίζει στον server και ο
            // χειριστής πάει εκεί που φαίνεται — στο «Ιστορικό».
            if (pushRunId !== null) {
              setPushRunId(null);
              setPushCanaryStoreId(null);
              setView('history');
              loadRuns();
            }
          }}
          onRerun={runDryRun}
          canPush={canManage}
          serverCanPush={serverCanPush}
          pushing={pushing}
          pushError={pushError}
          onPush={doPush}
          pushProgress={pushProgress}
          canaryStoreName={pushCanaryStoreId === null ? null : storeNameOf(pushCanaryStoreId)}
          syncHeader={syncHeader}
        />
      )}

      {syncOpen && (
        <PriceSyncModal
          index={priceIndex}
          storeId={priceStoreId}
          storeName={priceStoreName}
          canDecide={canManage && editingAllowed}
          busy={syncBusy || overridesSaving}
          error={syncError}
          draftFor={draftFor}
          isDirty={isDirty}
          onDraftChange={handleDraftChange}
          dirtyCount={dirtyOverrides.length}
          onClose={() => { setSyncOpen(false); setSyncError(null); }}
          onConfirm={confirmSync}
        />
      )}

      {migrateStoreId !== null && (
        <MigrateModal
          key={migrateStoreId}
          storeId={migrateStoreId}
          storeName={migrateStore ? migrateStore.store_name : `#${migrateStoreId}`}
          preview={migratePreview}
          loadingPreview={migratePreviewLoading}
          running={migrateRunning}
          result={migrateResult}
          // ΟΧΙ ξένη πρόοδος: αν τρέχει μετάπτωση ΑΛΛΟΥ καταστήματος στο
          // παρασκήνιο, το παράθυρο αυτού εδώ δεν την εμφανίζει ως δική του.
          progress={migrateBgStoreId === null || migrateBgStoreId === migrateStoreId
            ? migrateProgress : null}
          error={migrateError}
          // ΟΣΟ ΤΡΕΧΕΙ, ΔΕΝ ΚΛΕΙΝΕΙ: το παράθυρο είναι η μόνη ένδειξη ότι μια μη
          // αναστρέψιμη κλήση είναι σε εξέλιξη. Κλείσιμο στη μέση θα άφηνε τον
          // χειριστή να πατήσει «Μετάπτωση» δεύτερη φορά στο ίδιο κατάστημα.
          //
          // ΑΛΛΑ «τρέχει» ≠ «δεν ξέρω»: με κομμένο δίκτυο ή νεκρή διεργασία
          // server (`runWatchStuck`) η τελική κατάσταση δεν έρχεται ΠΟΤΕ και το «όσο
          // τρέχει» κλείδωνε ολόκληρη την καρτέλα μέχρι το F5 — με το ίδιο το παράθυρο
          // να λέει «δες το Ιστορικό».
          onClose={() => {
            if (migrateRunning && !migrateStuck) return;
            closeMigrate();
          }}
          onGoToHistory={() => { closeMigrate(); setView('history'); loadRuns(); }}
          onReloadPreview={() => { if (migrateStoreId !== null) loadMigratePreview(migrateStoreId); }}
          onConfirm={confirmMigrate}
        />
      )}
    </CatalogSheet>
  );
};

export default OrgPlatformCatalogTab;
